/**
 * Позиции сметы под работу (ПЛ-01, ПЛ-13): связь работы с позициями смет проекта и назначения людей,
 * техники и материалов по нормам сметы одной кнопкой. Цены сметы здесь не показываются.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { Button, IconButton } from '../../ui/button';
import { toast } from '../../ui/feedback';
import { Input } from '../../ui/form';

interface Item {
  id: string;
  estimateId: string;
  estimateCode: string;
  number: number;
  code: string | null;
  name: string;
  unit: string;
  quantity: number;
  resources: Array<{ kind: string; code: string; name: string; unit: string; quantity: number }>;
}
const n = (x: number) => x.toLocaleString('ru-RU', { maximumFractionDigits: 2 });

export function ActivityScope({
  scheduleId,
  activityId,
  canEdit,
}: {
  scheduleId: string;
  activityId: string;
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const key = ['activity-scope', activityId];
  const q = useQuery({
    queryKey: key,
    queryFn: () => api<Item[]>(`/activities/${activityId}/estimate-items`),
    retry: false,
  });
  const [text, setText] = useState('');
  const [term, setTerm] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setTerm(text.trim()), 400);
    return () => clearTimeout(t);
  }, [text]);
  const found = useQuery({
    queryKey: ['estimate-items-search', scheduleId, term],
    queryFn: () =>
      api<Array<Omit<Item, 'resources' | 'estimateId'>>>(`/schedules/${scheduleId}/estimate-items`, {
        query: { q: term },
      }),
    enabled: canEdit && term.length >= 2,
    retry: false,
  });
  const link = useMutation({
    mutationFn: (b: { itemId: string; remove?: boolean }) =>
      api<Item[]>(`/activities/${activityId}/estimate-items`, { body: b }),
    onSuccess: (rows) => {
      qc.setQueryData(key, rows);
      setText('');
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  const assign = useMutation({
    mutationFn: () =>
      api<{ created: number; updated: number; removed: number; missing: string[] }>(
        `/activities/${activityId}/estimate-items/assign`,
        { body: {} },
      ),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['assignments', scheduleId] });
      qc.invalidateQueries({ queryKey: ['resources'] });
      qc.invalidateQueries({ queryKey: ['resource-load'] });
      toast(
        `Назначения из сметы: новых ${r.created}, обновлено ${r.updated}${r.removed ? `, снято ${r.removed}` : ''}${
          r.missing.length
            ? `; нет в справочнике ресурсов: ${r.missing.slice(0, 3).join(', ')}${r.missing.length > 3 ? '…' : ''}`
            : ''
        }`,
        r.missing.length ? 'info' : 'success',
      );
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  // Без права на сметы блок не показывается
  if (q.isError && q.error instanceof ApiError && q.error.status === 403) return null;
  if (!q.data) return null;
  const linked = new Set(q.data.map((i) => i.id));
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-[14px] font-semibold">Позиции сметы</h3>
      {q.data.length ? (
        <ul className="divide-y divide-line text-[14px]">
          {q.data.map((i) => (
            <li key={i.id} className="flex items-start justify-between gap-2 py-1.5">
              <span className="min-w-0">
                <Link
                  to="/estimates/$estimateId"
                  params={{ estimateId: i.estimateId }}
                  className="inline-flex min-h-11 items-center hover:underline md:min-h-0"
                >
                  {i.estimateCode} · поз. {i.number} {i.code ? `${i.code} ` : ''}
                  {i.name}
                </Link>
                <span className="block text-[12px] text-ink-2">
                  {n(i.quantity)} {i.unit}
                  {i.resources.length
                    ? ` · ${i.resources.map((r) => `${r.name} ${n(r.quantity)} ${r.unit}`).join('; ')}`
                    : ''}
                </span>
              </span>
              {canEdit ? (
                <IconButton
                  label="Отвязать позицию"
                  onClick={() => link.mutate({ itemId: i.id, remove: true })}
                  className="size-11 shrink-0 md:size-9"
                >
                  <Trash2 className="size-4" />
                </IconButton>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[13px] text-ink-2">Позиции сметы не привязаны.</p>
      )}
      {canEdit ? (
        <>
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Найти позицию сметы: шифр сметы, код нормы или название"
            aria-label="Найти позицию сметы"
            className="max-md:h-11"
          />
          {term.length >= 2 && found.data ? (
            found.data.filter((x) => !linked.has(x.id)).length ? (
              <ul className="max-h-48 overflow-y-auto rounded-md border border-line">
                {found.data
                  .filter((x) => !linked.has(x.id))
                  .map((x) => (
                    <li key={x.id}>
                      <button
                        type="button"
                        onClick={() => link.mutate({ itemId: x.id })}
                        className="flex min-h-11 w-full flex-col items-start px-3 py-1 text-left text-[13px] hover:bg-surface-2 md:min-h-0"
                      >
                        <span>
                          {x.estimateCode} · поз. {x.number} {x.code ? `${x.code} ` : ''}
                          {x.name}
                        </span>
                        <span className="text-[12px] text-ink-2">
                          {n(x.quantity)} {x.unit}
                        </span>
                      </button>
                    </li>
                  ))}
              </ul>
            ) : (
              <p className="text-[12px] text-ink-2">Не найдено в сметах проекта</p>
            )
          ) : null}
          {q.data.length ? (
            <Button
              size="sm"
              loading={assign.isPending}
              onClick={() => assign.mutate()}
              className="self-start max-md:h-11"
            >
              Назначить людей, технику и материалы по смете
            </Button>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
